import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { CHART_COLORS, getConversionRate } from "../../utils/analyticsCharts";

const tooltipStyle = {
  background: "#fff",
  border: "1px solid #e2e8f0",
  borderRadius: "10px",
  boxShadow: "0 4px 16px rgba(48, 84, 120, 0.12)",
  fontSize: "0.82rem",
};

function ChartCard({ title, hint, children }) {
  return (
    <article className="app-analytics-chart">
      <div className="app-analytics-chart__head">
        <h4 className="app-analytics-chart__title">{title}</h4>
        {hint ? <p className="app-analytics-chart__hint">{hint}</p> : null}
      </div>
      <div className="app-analytics-chart__body">{children}</div>
    </article>
  );
}

export default function AnalyticsCharts({ charts }) {
  if (!charts) return null;

  const conversionRate = getConversionRate(charts.completionSplit);

  return (
    <section className="app-analytics__charts">
      <div className="app-analytics__section-head">
        <h3>Графики</h3>
      </div>

      <div className="app-analytics-charts__grid">
        <ChartCard
          title="Запуски и завершения"
          hint="Динамика за последние 14 дней"
        >
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={charts.daily}>
              <defs>
                <linearGradient id="startsGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={CHART_COLORS.starts} stopOpacity={0.35} />
                  <stop offset="95%" stopColor={CHART_COLORS.starts} stopOpacity={0.02} />
                </linearGradient>
                <linearGradient
                  id="completionsGradient"
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1"
                >
                  <stop offset="5%" stopColor={CHART_COLORS.completions} stopOpacity={0.35} />
                  <stop
                    offset="95%"
                    stopColor={CHART_COLORS.completions}
                    stopOpacity={0.02}
                  />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
              <XAxis
                dataKey="date"
                tick={{ fill: "#64748b", fontSize: 11 }}
                axisLine={false}
                tickLine={false}
                interval="preserveStartEnd"
              />
              <YAxis
                tick={{ fill: "#64748b", fontSize: 11 }}
                axisLine={false}
                tickLine={false}
                width={32}
              />
              <Tooltip contentStyle={tooltipStyle} />
              <Legend
                wrapperStyle={{ fontSize: "0.82rem", paddingTop: 8 }}
                iconType="circle"
              />
              <Area
                type="monotone"
                dataKey="starts"
                name="Запуски"
                stroke={CHART_COLORS.starts}
                fill="url(#startsGradient)"
                strokeWidth={2}
              />
              <Area
                type="monotone"
                dataKey="completions"
                name="Завершения"
                stroke={CHART_COLORS.completions}
                fill="url(#completionsGradient)"
                strokeWidth={2}
              />
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard
          title="Воронка прохождения"
          hint="Сколько игроков доходит до каждого этапа"
        >
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={charts.funnel} barCategoryGap="18%">
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
              <XAxis
                dataKey="stage"
                tick={{ fill: "#64748b", fontSize: 11 }}
                axisLine={false}
                tickLine={false}
                interval={0}
                angle={-12}
                textAnchor="end"
                height={56}
              />
              <YAxis
                tick={{ fill: "#64748b", fontSize: 11 }}
                axisLine={false}
                tickLine={false}
                width={36}
              />
              <Tooltip contentStyle={tooltipStyle} />
              <Bar dataKey="value" name="Игроков" radius={[8, 8, 0, 0]}>
                {charts.funnel.map((entry, index) => (
                  <Cell
                    key={entry.stage}
                    fill={
                      index === charts.funnel.length - 1
                        ? CHART_COLORS.completions
                        : index === 0
                          ? CHART_COLORS.starts
                          : CHART_COLORS.inProgress
                    }
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard
          title="Точки выхода из сценария"
          hint="На каких сценах чаще всего бросают прохождение"
        >
          <ResponsiveContainer width="100%" height={260}>
            <BarChart
              data={charts.sceneDropoff}
              layout="vertical"
              margin={{ left: 8, right: 12 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={false} />
              <XAxis
                type="number"
                tick={{ fill: "#64748b", fontSize: 11 }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                type="category"
                dataKey="scene"
                tick={{ fill: "#64748b", fontSize: 11 }}
                axisLine={false}
                tickLine={false}
                width={108}
              />
              <Tooltip contentStyle={tooltipStyle} />
              <Bar
                dataKey="dropoffs"
                name="Выходов"
                fill={CHART_COLORS.dropped}
                radius={[0, 8, 8, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard
          title={`Конверсия: ${conversionRate}%`}
          hint="Доля игроков, дошедших до финала"
        >
          <div className="app-analytics-chart__split">
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie
                  data={charts.completionSplit}
                  dataKey="value"
                  nameKey="name"
                  innerRadius={58}
                  outerRadius={88}
                  paddingAngle={3}
                >
                  {charts.completionSplit.map((entry, index) => (
                    <Cell
                      key={entry.name}
                      fill={CHART_COLORS.pie[index % CHART_COLORS.pie.length]}
                    />
                  ))}
                </Pie>
                <Tooltip contentStyle={tooltipStyle} />
                <Legend
                  wrapperStyle={{ fontSize: "0.82rem" }}
                  iconType="circle"
                />
              </PieChart>
            </ResponsiveContainer>

            <div className="app-analytics-chart__errors">
              <h5>Ошибки за сессию</h5>
              <ul className="app-analytics-chart__errors-list">
                {charts.errorsBySession.map((item) => {
                  const max = Math.max(
                    ...charts.errorsBySession.map((row) => row.count),
                    1
                  );
                  const width = Math.round((item.count / max) * 100);

                  return (
                    <li key={item.label}>
                      <span>{item.label}</span>
                      <div className="app-analytics-chart__errors-bar">
                        <span style={{ width: `${width}%` }} />
                      </div>
                      <strong>{item.count}</strong>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        </ChartCard>
      </div>
    </section>
  );
}
